import { useEffect, useState, useCallback } from "react";

/**
 * Filter state hook shared by LeadsPerformancePage and PredictivePage.
 *
 * Behavior:
 * - Persists the whole filter object to localStorage under `storageKey`.
 * - Merges saved values on top of `defaults` so newly added filter keys
 *   don't blow up when older sessions are loaded.
 * - `setF(key, value)` normalises any input shape from a MultiSelect
 *   (`"all"`, `"a,b"` CSV, or `["a", "b"]`) into a clean array of values
 *   without the "all" sentinel. This prevents the "dos seleccionadas"
 *   bug that came from ["all"] or ["a,b"] leaking into state.
 * - `reset()` returns the state to `defaults`.
 * - `toggleF(key, value)` adds/removes a single value from the array.
 *
 * @param {string} storageKey  localStorage key
 * @param {Object} defaults    filter shape, e.g. { year: [], brand: [], ... }
 * @param {string} [allSentinel="all"]  MultiSelect's "no selection" value
 */
export default function useNormalizedFilter(storageKey, defaults, allSentinel = "all") {
  const [filters, setFilters] = useState(() => {
    try {
      const saved = localStorage.getItem(storageKey);
      return saved ? { ...defaults, ...JSON.parse(saved) } : defaults;
    } catch {
      return defaults;
    }
  });

  useEffect(() => {
    try {
      localStorage.setItem(storageKey, JSON.stringify(filters));
    } catch {
      /* localStorage may be full/blocked — ignore. */
    }
  }, [storageKey, filters]);

  const setF = useCallback((k, v) => {
    setFilters((f) => {
      let arr = [];
      if (Array.isArray(v)) {
        arr = v.filter((x) => x && x !== allSentinel);
      } else if (typeof v === "string" && v && v !== allSentinel) {
        arr = v.split(",").map((s) => s.trim()).filter((s) => s && s !== allSentinel);
      }
      return { ...f, [k]: arr };
    });
  }, [allSentinel]);

  const toggleF = useCallback((k, v) => {
    setFilters((f) => {
      const cur = f[k] || [];
      return { ...f, [k]: cur.includes(v) ? cur.filter((x) => x !== v) : [...cur, v] };
    });
  }, []);

  const reset = useCallback(() => setFilters(defaults), [defaults]);

  return { filters, setFilters, setF, toggleF, reset };
}
